import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WorkProgressDialog({ 
  open, 
  onOpenChange, 
  currentLocation, 
  onClockOutSuccess 
}) {
  const [workProgress, setWorkProgress] = useState({
    pipe_insulation_ft: '',
    pvc_jacket_ft: '',
    duct_insulation_flexible_roll: '',
    duct_insulation_rigid_boxes: '',
    venture_clad_roll: '',
    canvas_roll: '',
    comments: ''
  });
  const [validationError, setValidationError] = useState('');

  // Check if at least one work field is filled
  const isFormValid = () => {
    const hasWorkEntry = 
      parseFloat(workProgress.pipe_insulation_ft) > 0 ||
      parseFloat(workProgress.pvc_jacket_ft) > 0 ||
      parseFloat(workProgress.duct_insulation_flexible_roll) > 0 ||
      parseFloat(workProgress.duct_insulation_rigid_boxes) > 0 ||
      parseFloat(workProgress.venture_clad_roll) > 0 ||
      parseFloat(workProgress.canvas_roll) > 0;
    return hasWorkEntry;
  };

  const submitClockOut = async () => {
    // Validate that at least one work field is filled
    if (!isFormValid()) {
      setValidationError('Please enter at least one work progress value before clocking out.');
      toast.error('Work progress is required. Please enter at least one value.');
      return;
    }
    setValidationError('');

    const token = localStorage.getItem('token');
    try {
      const workProgressData = {
        pipe_insulation_ft: parseFloat(workProgress.pipe_insulation_ft) || 0,
        pvc_jacket_ft: parseFloat(workProgress.pvc_jacket_ft) || 0,
        duct_insulation_flexible_roll: parseFloat(workProgress.duct_insulation_flexible_roll) || 0,
        duct_insulation_rigid_boxes: parseFloat(workProgress.duct_insulation_rigid_boxes) || 0,
        venture_clad_roll: parseFloat(workProgress.venture_clad_roll) || 0,
        canvas_roll: parseFloat(workProgress.canvas_roll) || 0,
        comments: workProgress.comments
      };

      const response = await fetch(`${API_URL}/api/time/clock-out`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          address: currentLocation.address,
          work_progress: workProgressData
        })
      });

      if (response.ok) {
        toast.success('Clocked out successfully with work progress saved!');
        setWorkProgress({
          pipe_insulation_ft: '',
          pvc_jacket_ft: '',
          duct_insulation_flexible_roll: '',
          duct_insulation_rigid_boxes: '',
          venture_clad_roll: '',
          canvas_roll: '',
          comments: ''
        });
        onClockOutSuccess();
      } else {
        const error = await response.json();
        if (Array.isArray(error.detail)) {
          const errorMessages = error.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else {
          toast.error(error.detail || 'Failed to clock out');
        }
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>📊 Enter Your Work Progress</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Please enter the work you completed today. <span className="text-red-600 font-semibold">At least one field is required</span> to clock out.
          </p>

          {validationError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{validationError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Pipe Insulation (ft)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                placeholder="0"
                value={workProgress.pipe_insulation_ft}
                onChange={(e) => setWorkProgress({...workProgress, pipe_insulation_ft: e.target.value})}
              />
            </div>

            <div>
              <Label>PVC Jacket (ft)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                placeholder="0"
                value={workProgress.pvc_jacket_ft}
                onChange={(e) => setWorkProgress({...workProgress, pvc_jacket_ft: e.target.value})}
              />
            </div>

            <div>
              <Label>Duct Insulation Flexible (Roll)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                placeholder="0"
                value={workProgress.duct_insulation_flexible_roll}
                onChange={(e) => setWorkProgress({...workProgress, duct_insulation_flexible_roll: e.target.value})}
              />
            </div>

            <div>
              <Label>Duct Insulation Rigid (Boxes)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                placeholder="0"
                value={workProgress.duct_insulation_rigid_boxes}
                onChange={(e) => setWorkProgress({...workProgress, duct_insulation_rigid_boxes: e.target.value})}
              />
            </div>

            <div>
              <Label>Venture Clad (Roll)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                placeholder="0"
                value={workProgress.venture_clad_roll}
                onChange={(e) => setWorkProgress({...workProgress, venture_clad_roll: e.target.value})}
              />
            </div>

            <div>
              <Label>Canvas (Roll)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                placeholder="0"
                value={workProgress.canvas_roll}
                onChange={(e) => setWorkProgress({...workProgress, canvas_roll: e.target.value})}
              />
            </div>
          </div>

          <div>
            <Label>Comments / Notes</Label>
            <Textarea
              placeholder="Add any additional comments about today's work..."
              rows={4}
              value={workProgress.comments}
              onChange={(e) => setWorkProgress({...workProgress, comments: e.target.value})}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                if (!isFormValid()) {
                  toast.error('Please enter at least one work progress value before clocking out or canceling.');
                  setValidationError('Work progress is mandatory. Please enter at least one value.');
                } else {
                  onOpenChange(false);
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="button"
              onClick={submitClockOut}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!isFormValid()}
            >
              Submit & Clock Out
            </Button>
          </div>
          <p className="text-xs text-gray-500 text-center">
            💡 You must enter work progress to complete your clock out.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
